import * as React from 'react'
import { useState, useEffect } from 'react'
import { t } from 'ttag'
import { getFormattedUrl } from '../../common/helpers/integrationUrl'
import { redirectUrl } from '../../common/helpers/routeHelpers'
import { ROUTES } from '../../common/Routes/routePaths'
import { Card } from '../../components/Card/Card'
import Loader from '../../components/Loader/Loader'
import {
  CompanyCountriesDto,
  CompanyId,
  DivisionDto,
  DivisionRow,
  DivisionId,
  DivisionCategoryId,
  DivisionCategoryRow,
  Gender,
  Lang,
  OnboardingStatus,
  RegisterUser$Request,
  ServiceProvider,
  SetUserThirdPartyIntegration$Request,
  Units,
  UserCompanyDto,
  UserProfileDto,
} from '../../services/api.types'
import './RegisterUser.scss'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import { store } from '../../index'
import { getUserCompanies } from './store/RegisterUserSelectors'
import { IGlobalState } from '../../Root/store/appReducer'
import { yuMuuvPublicCompanyId } from '../Settings/Settings'
import NotificationPreferencesWrapper from '../NotificationPreferences/NotificationPreferencesWrapper'
import IntegrationsWrapper from '../Integrations/IntegrationsWrapper'
import { DesktopSidebar } from '../../components/DesktopSidebar/DesktopSidebar'
import { resetCreateTeamLoading } from '../TeamManagement/store/TeamManagementActions'
import { ProfileForm } from './components/ProfileForm'
import { ActivityForm } from './components/ActivityForm'

export interface IStateProps {
  userDto: UserProfileDto
  userCompanies: UserCompanyDto[]
  countries: CompanyCountriesDto[]
  divisions: DivisionDto[]
  divisionCategories: DivisionCategoryRow[]
  //companyGroups: { [categoryId: string]: DivisionDto[] }
  userIntegrationScopes: string
  createdDivisionId: DivisionId
  lastUpdate: number | null
}

export interface IDispatchProps {
  registerUser(payload: any): void
  getWhoAmI(): void
  setIntegration(payload: SetUserThirdPartyIntegration$Request): void
  reLocate(): void
  getUserCompanyCountries(): void
  getDivisions(): void
  getDivisionCategories(): void
  updateProfileData(payload: any): void
  updateActivityData(payload: any): void
}

type IRegisterUserProps = IStateProps & IDispatchProps

export interface IRegisterUserState {
  password: string
  appleHealthPasswordError: boolean
  integration: string | null
  firstName: string
  lastName: string
  dateOfBirth: string
  dateOfBirthError: boolean
  gender: Gender.MALE | Gender.FEMALE
  units: Units.METRIC | Units.IMPERIAL
  weight: string | number
  weightLbs: string | number
  height: string | number
  heightFt: string | number
  heightIn: string | number
  isProfileFormValid: boolean
  activityDataFormValid: boolean
  country: string
  timeZone: string
  divisionId: DivisionId
  hasSingleCompany: boolean
  isActivityIndexFieldRequired: boolean
  companyId: CompanyId
  isSubmitInProgress: boolean
  createdDivisionIdToSelect: string | null
  selectedGroupIdsPerCategory: { [categoryId: string]: DivisionId | null }
}

const companyIdBetssonGroup = '6eb3c619-59cb-40c0-b87f-204877bfb5b5'
export const isBurgerHidden = (companyId: CompanyId) => {
  return companyId === companyIdBetssonGroup //|| companyId === companyIdYuMuuvTeam
}

export const isCompanyDivisionRequired = (companyId: CompanyId, companyDivisions?: DivisionDto[]) => {
  const userCompanies = getUserCompanies(store.getState() as any as IGlobalState)
  const userCompany = userCompanies.find((uc) => uc.companyId === companyId)

  // If there are no joinable teams, team selection is not required.
  if (companyDivisions && companyDivisions.length && userCompany && userCompany.maxTeamSize) {
    const maxTeamSize = userCompany.maxTeamSize
    if (
      companyDivisions.filter((division) => !division.locked && (!maxTeamSize || division.members < maxTeamSize))
        .length === 0
    ) {
      return false
    }
  }

  return !!userCompany?.divisionRequired
}

export const isCompanyCountryRequired = (companyId: CompanyId) => {
  const userCompanies = getUserCompanies(store.getState() as any as IGlobalState)
  const userCompany = userCompanies.find((uc) => uc.companyId === companyId)
  return !!userCompany?.countryRequired
}

export const isCompanyTeamSelectionLocked = (companyId: CompanyId) => {
  const userCompanies: UserCompanyDto[] = getUserCompanies(store.getState() as any as IGlobalState)
  const userCompany = userCompanies.find((uc) => uc.companyId === companyId)
  return !!userCompany?.teamSelectionLocked
}

export const isCompanyUserCreatedTeams = (companyId: CompanyId) => {
  const userCompanies: UserCompanyDto[] = getUserCompanies(store.getState() as any as IGlobalState)
  const userCompany = userCompanies.find((uc) => uc.companyId === companyId)
  return !!userCompany?.userCreatedTeams
}

export const getCompanyMaxTeamSize = (companyId: CompanyId) => {
  const userCompanies: UserCompanyDto[] = getUserCompanies(store.getState() as any as IGlobalState)
  const userCompany = userCompanies.find((uc) => uc.companyId === companyId)
  return userCompany?.maxTeamSize
}

export const RegisterUser: React.FC<IRegisterUserProps> = (props) => {
  // Component-level state (coordination and lifecycle)
  const [appleHealthPasswordError, setAppleHealthPasswordError] = useState<boolean>(false)
  const [integration, setIntegration] = useState<string | null>('')
  const [hasSingleCompany, setHasSingleCompany] = useState<boolean>(true)
  const [companyId, setCompanyId] = useState<CompanyId>('')
  const [isSubmitInProgress, setIsSubmitInProgress] = useState<boolean>(false)
  const [createdDivisionIdToSelect, setCreatedDivisionIdToSelect] = useState<string | null>(null)
  const [selectedUnits, setSelectedUnits] = useState<Units.METRIC | Units.IMPERIAL | null>(null)

  // Component mount effect - equivalent to componentDidMount
  useEffect(() => {
    document.body.classList.add('register-user-page')

    // Cleanup on unmount - equivalent to componentWillUnmount
    return () => {
      document.body.classList.remove('register-user-page')
    }
  }, [])

  // Initialize component - equivalent to componentDidMount async logic
  useEffect(() => {
    const initializeComponent = async () => {
      store.dispatch({ type: String(resetCreateTeamLoading) })

      const { setIntegration, reLocate, createdDivisionId, getUserCompanyCountries, getDivisions } = props
      reLocate()
      const { authorizationCode, serviceProvider } = await getFormattedUrl()
      const redirectUrlString = `${window.location.origin}${ROUTES.INTEGRATION_PROXY.to}`

      if (authorizationCode) {
        setIntegration({ authorizationCode, serviceProvider, redirectUrl: redirectUrlString })
      } else {
        getUserCompanyCountries()
        getDivisions()
      }

      // Fetch divisions & division categories
      if (companyId) {
        props.getDivisionCategories()
      }
    }

    initializeComponent()
  }, []) // Only run on mount - these props are stable from Redux connect

  // Handle createdDivisionId from props - equivalent to componentDidUpdate
  useEffect(() => {
    if (props.createdDivisionId && props.createdDivisionId !== createdDivisionIdToSelect) {
      props.getDivisions()
      setCreatedDivisionIdToSelect(props.createdDivisionId)
    }
  }, [props.createdDivisionId, createdDivisionIdToSelect, props.getDivisions])

  // Handle companyId changes - fetch division categories when companyId changes
  useEffect(() => {
    if (companyId) {
      props.getDivisionCategories()
    }
  }, [companyId, props.getDivisionCategories])

  // Handle lastUpdate prop change - clear isSubmitInProgress on error
  useEffect(() => {
    if (props.lastUpdate !== undefined) {
      setIsSubmitInProgress(false)
    }
  }, [props.lastUpdate])

  // Handle userCompanies prop change - set companyId
  useEffect(() => {
    const uc = props.userCompanies.filter((uc: { companyId: string }) => uc.companyId !== yuMuuvPublicCompanyId)
    let newCompanyId = uc.find((uc: { companyId: string }) => uc.companyId !== yuMuuvPublicCompanyId)?.companyId
    if (!newCompanyId) newCompanyId = yuMuuvPublicCompanyId

    if (newCompanyId !== companyId) {
      setCompanyId(newCompanyId)
      props.getDivisionCategories()
    }
  }, [props.userCompanies, companyId, props])

  // Handle userDto serviceProvider prop change - set integration state
  useEffect(() => {
    const { userDto } = props
    const { serviceProvider } = userDto

    // @ts-ignore
    if (serviceProvider !== '' && serviceProvider === ServiceProvider.APPLE_HEALTH) {
      if (integration !== serviceProvider) setIntegration(serviceProvider)
    }

    // Determine if user has single company
    const uc = props.userCompanies.filter((uc: { companyId: string }) => uc.companyId !== yuMuuvPublicCompanyId)
    const hasSingleCompanyValue = uc && uc.length === 1
    setHasSingleCompany(hasSingleCompanyValue)
  }, [props.userDto.serviceProvider, props.userCompanies, integration])

  const getOnboardingStep = () => {
    const { userDto } = props
    const { onboardingStatus } = userDto
    // const path = redirectUrl()
    // console.log('getOnboardingStep')

    switch (onboardingStatus) {
      case OnboardingStatus.PROFILE_DATA: {
        return (
          <ProfileForm
            userDto={props.userDto}
            userCompanies={props.userCompanies}
            countries={props.countries}
            divisions={props.divisions}
            divisionCategories={props.divisionCategories}
            companyId={companyId}
            hasSingleCompany={hasSingleCompany}
            createdDivisionId={props.createdDivisionId}
            lastUpdate={props.lastUpdate}
            setIsSubmitInProgress={setIsSubmitInProgress}
            registerUser={props.registerUser}
            onUnitsChange={setSelectedUnits}
          />
        )
      }
      case OnboardingStatus.ACTIVITY_DATA: {
        return (
          <ActivityForm
            userDto={props.userDto}
            userCompanies={props.userCompanies}
            lastUpdate={props.lastUpdate}
            setIsSubmitInProgress={setIsSubmitInProgress}
            updateActivityData={props.updateActivityData}
            initialUnits={selectedUnits}
          />
        )
      }
      case OnboardingStatus.NOTIFICATION_PREFERENCES: {
        return <NotificationPreferencesWrapper isDuringRegistration={true} />
      }
      case OnboardingStatus.INTEGRATION: {
        return <IntegrationsWrapper isDuringRegistration={true} />
      }
      default:
        return (
          <div className="loader-wrapper text-center">
            {/*<div style={{ maxWidth: '75%', marginLeft: 'auto', marginRight: 'auto', marginBottom: 10 }}>
              {t`If you've been on this page for more than 5 seconds, then click here`}:
            </div>
            <Button text={t`Back`} url={path} />*/}
            <Loader />
          </div>
        )
    }
  }

  // Main component render
  return (
    <Card className="onboarding">
      <div className="onboarding__form">
        <NotificationsWrapper />
        {getOnboardingStep()}
      </div>
      {/*<div className="onboarding__sidebar" />*/}
      <DesktopSidebar />
    </Card>
  )
}
